import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface PlayerErrorBoundaryProps {
  children: ReactNode;
}

interface PlayerErrorBoundaryState {
  hasError: boolean;
}

export function redactPlayerErrorUrl(value: string): string {
  try {
    const url = new URL(value, window.location.origin);
    url.searchParams.delete("ref");
    const fragment = new URLSearchParams(url.hash.replace(/^#/, ""));
    fragment.delete("ref");
    url.hash = fragment.toString();
    return url.toString();
  } catch {
    return "";
  }
}

export class PlayerErrorBoundary extends Component<
  PlayerErrorBoundaryProps,
  PlayerErrorBoundaryState
> {
  state: PlayerErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): PlayerErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[PlayerErrorBoundary]", error, info.componentStack);
    fetch("/bff/error", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        message: error.message,
        stack: error.stack?.slice(0, 1000) ?? null,
        url: redactPlayerErrorUrl(window.location.href),
      }),
    }).catch(() => {});
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <Stack
        component="main"
        spacing={2}
        sx={{
          minHeight: "100dvh",
          px: 3,
          textAlign: "center",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography component="h1" variant="h2">
          Something went wrong
        </Typography>
        <Typography color="text.secondary">
          An unexpected error occurred. Reload the page to try again.
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Reload page
        </Button>
      </Stack>
    );
  }
}
